import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { submitFormJson, getContentByUrl, postJsonData, submitFormJsonWithAbort } from 'widgets/toolbox/ajax';

import { TModalInstance } from 'widgets/global/Modal';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TConfirmDialogInstance } from 'widgets/global/ConfirmDialog';
import { TInputStepperInstance } from 'widgets/forms/InputStepper';
import { TProductDeliveryOptionsInstance } from '../product/ProductDeliveryOptions';
import { showErrorLayout } from 'widgets/toolbox/util';

export type TCart = import('widgets/cart/CartMgr').TCart & {
    pageSuccess: string;
    emptyCartImageUrl: string;
    idmeError: string;
    isRewardCouponAction: boolean;
};

export type TAttributes = {
    'data-tau'?: string;
    url?: string;
    pid?: string;
    uuid?: string;
    currentValue?: number;
    minOrderQuantity?: number | null;
    optionId?: string;
    optionVal?: string;
    customOptionValue?: string;
    lowest30daysPriceMessage?: string;
    isPriceAtLowestLevel?: string;
    isDisplay30DaysPriceCart?: string;
    showStrikePriceAndBadge?: string;
    lineItemMinOrderQuantity?: number;
    shortName?: string;
    productModelName?: string;
    imageUrl?: string;
    imageAlt?: string;
    imageSources?: Array<{
        media: string;
        srcset: string;
    }>;
    pdpLink?: string;
    variationAttributes?: Array<{
        name: string;
        value: string;
    }>;
    price?: {
        isDefaultPrice?: string;
        isRangePrice?: string;
        list?: {
            formatted?: string;
        };
        sales?: {
            formatted?: string;
            discount?: string;
        };
        min?: {
            sales?: {
                formatted?: string;
            };
        };
        max?: {
            sales?: {
                formatted?: string;
            };
        };
    };
};

export type TProductAttributes = Record<string, string | number | boolean>;

type TSwitchDelivertyTypeRequestData = {
    uuid: string;
    isBOPISSelected: string;
};

export interface ICartInputStepperInstance extends TInputStepperInstance {
    prefs(): ReturnType<TInputStepperInstance['prefs']> & {
        lineItemMinOrderQuantity?: number;
    };
}

const CART_PAGE_NOTIFICATIONS = 'cart.page.notification';
const SCREEN_READER_ALERT = 'screen-reader';

/**
 * @description CoreCartMgr implementation
 * @param CartMgr Base widget for extending
 * @returns Core Cart Manager class
 */
function CoreCartMgrClassCreator(CartMgr: import('widgets/cart/CartMgr').TCartMgr) {
    /**
     * @category widgets
     * @subcategory cart
     * @class CoreCartMgr
     * @augments CartMgr
     * @classdesc CartMgr component on Cart page. Renders the whole cart template on each update e.g. quantity/coupon/product edit etc.
     * Widget manages next actions:
     * 1. Edit product in a cart
     * 2. Change product quantity
     * 3. Remove product (move to Wish list) from Cart
     * 4. Communicate actions results
     * 5. Re-render Cart including products list and cart summary in case of actions like: product updated, coupon applied etc.
     * 6. Enable/disable "Secure Checkout" button to move to Checkout process depending on if there are errors in cart, which prevents moving to Checkout
     * @property {string} data-widget - Widget name `cartMgr`
     * @property {Array} data-items - Cart items list
     * @property {Array} data-approaching-discounts - Cart approaching discounts list
     * @property {object} data-action-urls - Cart action URLs
     * @property {object} data-totals - Cart totals
     * @property {Array} data-shipments - Cart shipments list
     * @property {boolean} data-valid - Is Cart valid
     * @property {number} data-num-items - Number of items in Cart
     * @property {string} data-cart-get-url - An URL to get updated Cart object from server
     * @property {string} data-cross-sell-url - An URL to get cross-sell recommendation slot for the current Cart
     * @property {object} data-accessibility-alerts - Accessibility alerts messages for different user actions
     * @property {string} data-empty-cart-image-url - An URL to get image for empty Cart
     * Possible values are: `quantitychanged`, `productupdated`, `productremoved`,
     * `promocodeadded`, `promocodeapplied`, `promocoderemoved`, `addedtowishlist`
     *
     * @example <caption>Example of CartMgr widget markup</caption>
     * <div
     *     data-widget="cartMgr"
     *     data-items="${JSON.stringify(pdict.items.toArray().concat(pdict.giftCertificateItems ? pdict.giftCertificateItems.toArray() : []))}"
     *     data-approaching-discounts="${JSON.stringify(pdict.approachingDiscounts ? pdict.approachingDiscounts.toArray() : [])}"
     *     data-action-urls="${JSON.stringify(pdict.actionUrls)}"
     *     data-cart-get-url="${URLUtils.url('Cart-Get')}"
     *     data-totals="${JSON.stringify(pdict.totals)}"
     *     data-shipments="${JSON.stringify(pdict.shipments ? pdict.shipments.toArray() : [])}"
     *     data-valid="${JSON.stringify(pdict.valid)}"
     *     data-num-items="${JSON.stringify(pdict.numItems)}"
     *     data-accessibility-alerts='{
     *         "quantitychanged": "${Resource.msg('alert.quantitychanged', 'cart', null)}",
     *         ...
     *     }'
     *     data-empty-cart-image-url="${pdict.emptyCartImageUrl}"
     * >
     *     <div data-ref="cartContainer">
     *         <isprint value="${pdict.cartTemplateMustache}" encoding="off"/>
     *     </div>
     *     <noscript data-ref="cartTemplate" hidden="hidden">
     *          <isprint value="${pdict.cartTemplateISML}" encoding="off"/>
     *     </noscript>
     *
     *      <isinclude template="cart/cartRemoveProductModal"/>
     *      <isinclude template="cart/cartEditProductModal"/>
     *
     *      <script type="template/mustache" data-ref="errorTemplate">
     *          <div data-ref="errorMsgs">
     *              <div role="alert">
     *                  {{message}}
     *              </div>
     *          </div>
     *      </script>
     * </div>
     */
    class CoreCartMgr extends CartMgr {
        removeButton?: TRefElementInstance | TInputStepperInstance;

        crossSellAnchorsKey = '';

        crossSellRequestId = 0;

        prefs() {
            return {
                continueShoppingUrl: '',
                crossSellUrl: '',
                isBasketMerged: false,
                isFitGuideAvailable: false,
                isOutOfStock: false,
                pageSuccess: <TCart['pageSuccess']>'',
                classesModalQuickViewCart: 'm-quick_view_cart',
                classesModalFor1774Product: 'm-1774-product',
                classesModalForCareEssentialsProduct: 'm-care_essentials-product',
                classesModalCartRecommendation: 'm-cart_recommendation',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init() {
            super.init();
            this.setContinueShoppingUrl();
            this.crossSellAnchorsKey = this.getCrossSellAnchorsKey(this.prefs().items);
            this.eventBus().on('express.checkout.basket.started', 'sendGTMForExpressCheckout');

            if (this.prefs().isBasketMerged) {
                this.eventBus().emit(CART_PAGE_NOTIFICATIONS, this, 'cart_merge');
            }

            if (this.prefs().isFitGuideAvailable) {
                this.eventBus().emit(CART_PAGE_NOTIFICATIONS, this, 'consecutive_sizes');
            }

            if (this.prefs().isOutOfStock) {
                this.eventBus().emit(CART_PAGE_NOTIFICATIONS, this, 'unavailable_quantity');
            }
        }

        /**
         * @description Delivery method updated handler on item in cart
         * @param deliveryMethodWidget - Delivery method selector widget
         * @param isBOPISSelected is BOPIS selected
         * @param UUID product UUID
         */
        switchDeliveryType(deliveryMethodWidget: TProductDeliveryOptionsInstance, isBOPISSelected: boolean, UUID: string): void {
            const requestData: TSwitchDelivertyTypeRequestData = {
                uuid: UUID,
                isBOPISSelected: isBOPISSelected.toString()
            };

            const switchDeliveryTypeUrl = deliveryMethodWidget.prefs();

            this.showProgressBar();

            postJsonData(switchDeliveryTypeUrl.switchDeliveryTypeUrl, requestData)
                .then(response => {
                    this.renderCart(response);
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Sets the "Continue Shopping" link URL if the preference is defined.
         * Updates the `href` attribute of the corresponding link element.
         */
        setContinueShoppingUrl() {
            const continueShoppingUrl = this.prefs().continueShoppingUrl;

            if (continueShoppingUrl) {
                const continueShoppingLink = this.ref('continueShoppingUrl');

                continueShoppingLink.get()?.setAttribute('href', continueShoppingUrl);
            }
        }

        /**
         * @description Executes on product updated in Quick View
         * @param response Response object from server
         */
        onProductUpdated(response: (TResponse & TCart) | (TResponse & unknown)) {
            const PLI_EDITING_ACTION = 'Cart-EditProductLineItem';
            const GIFT_EDITING_ACTION = 'GiftCertificate-Submit';

            if (![PLI_EDITING_ACTION, GIFT_EDITING_ACTION].includes(response.action)) {
                return;
            }

            const cart = <TResponse & TCart>response;

            if (cart) {
                this.renderCart(cart).then(() => this.accessibilityAlert(this.prefs().accessibilityAlerts.productupdated));
            }
        }

        /**
         * @description Executes on product updated in Quick View
         * @param cart Cart object
         */
        onCouponUpdated(cart: TCart) {
            this.renderCart(cart).then(() => {
                if (cart && cart.couponAction === 'applied') {
                    this.eventBus().emit('show.coupon.applied.message', cart);
                }

                if (cart && cart.couponAction === 'removed') {
                    if (cart.isRewardCouponAction) {
                        this.getByIdDeep('yotpo-rewards-wrapper', rewardsWrapper => {
                            rewardsWrapper.ref('disclosureButton').focus();
                        });

                        return;
                    }

                    this.getByIdDeep('coupon-wrapper', couponWrapper => {
                        couponWrapper.ref('disclosureButton').focus();
                    });
                }
            });
        }

        /**
         * @description Builds a key from the cart products, which are used as Einstein anchors for the cross-sell recommendation.
         * Bonus products are skipped, same as on the server side.
         * @param items - Cart items
         * @returns Key representing the cart products
         */
        getCrossSellAnchorsKey(items: TCart['items']): string {
            return items
                .filter(item => !item.isBonusProductLineItem)
                .map(item => item.id)
                .join(',');
        }

        /**
         * @description Re-renders the cross-sell recommendation slot when cart products are changed,
         * so Einstein receives updated anchors and the recommendation reflects the current cart.
         * The slot is excluded from cart re-rendering by `data-skip-render`, so it is updated separately.
         * @param cart - Rendered Cart object
         */
        refreshCrossSell(cart: TCart) {
            const anchorsKey = this.getCrossSellAnchorsKey(cart.items);

            if (anchorsKey === this.crossSellAnchorsKey) {
                return;
            }

            this.crossSellAnchorsKey = anchorsKey;

            if (!anchorsKey || !this.prefs().crossSellUrl) {
                return;
            }

            this.has('crossSellCart', crossSellCart => {
                const requestId = ++this.crossSellRequestId;

                getContentByUrl(this.prefs().crossSellUrl)
                    .then(response => {
                        // skip outdated response in case of a few cart updates in a row
                        if (requestId === this.crossSellRequestId) {
                            this.renderCrossSell(crossSellCart, response);
                        }
                    })
                    .catch(() => {
                        // keep the current recommendation if the request failed
                    });
            });
        }

        /**
         * @description Renders Einstein recommendation slot markup and executes its scripts.
         * Platform renders Einstein slot as an empty placeholder and an inline script (without `type` attribute),
         * which requests recommendations (Einstein `viewReco` activity) and fills the placeholder asynchronously.
         * Scripts inserted via `innerHTML` are not executed, so they are re-created.
         * `RefElement.append` can't be used, since it executes only scripts with `type="text/javascript"`.
         * @param crossSellCart - Cross-sell slot container
         * @param html - Rendered slot markup
         */
        renderCrossSell(crossSellCart: TRefElementInstance, html: string) {
            const crossSellCartEl = crossSellCart.get();

            if (!crossSellCartEl) {
                return;
            }

            crossSellCartEl.innerHTML = html;

            crossSellCartEl.querySelectorAll('script:not([type]), script[type="text/javascript"]').forEach(script => {
                const executableScript = document.createElement('script');

                executableScript.text = script.textContent || '';
                script.replaceWith(executableScript);
            });
        }

        /**
         * @description Renders Cart object and saves it as a current cart state
         * Adds some information into Cart object prior to rendering,
         * to make the whole object mustache template friendly
         * @emits "cart.updated"
         * @param cart - Cart object to reflect in template
         * @param updatePrefs - Update widget prefs according to new cart data
         * @returns Promise object represents cart rendering result
         */
        renderCart(cart: TCart, updatePrefs = true): Promise<void> {
            const emptyCartImageUrl = String(this.config.emptyCartImageUrl);

            if (!cart) {
                return Promise.reject(new Error());
            }

            if (updatePrefs) {
                this.setPrefs(cart);
            }

            if (!cart.items.length && emptyCartImageUrl) {
                cart.emptyCartImageUrl = emptyCartImageUrl;
            }

            cart.idmeError = this.config.idmeVerificationError ? String(this.config.idmeVerificationError) : '';

            return this.render('cartTemplate', cart, this.ref('cartContainer')).then(() => {
                if (!cart.items.length) {
                    this.setContinueShoppingUrl();
                    this.callIfExists('focusFirst');
                }

                this.refreshCrossSell(cart);

                /**
                 * @description Event, indicates that cart content was updated
                 * @event "cart.updated"
                 */
                this.eventBus().emit('cart.updated', cart);
            });
        }

        /**
         * @description Updates quantity of chosen product in Cart page
         * @param quantityStepper - Select quantity input
         * @returns Promise object represents server response with quantity updating result
         */
        updateQty(quantityStepper: ICartInputStepperInstance): Promise<null> | Promise<unknown> {
            this.showProgressBar();

            const url = quantityStepper.data<string>('action');
            const uuid = quantityStepper.data<string>('uuid');
            const pid = quantityStepper.data<string>('pid');
            const currentQuantity = quantityStepper.currentValue;

            const { lineItemMinOrderQuantity } = quantityStepper.prefs();

            const isQuantityBelowMinValue = lineItemMinOrderQuantity ? currentQuantity < lineItemMinOrderQuantity : false;

            if (currentQuantity === 0 || isQuantityBelowMinValue) {
                this.removeProduct(quantityStepper);
                this.hideProgressBar();

                return Promise.resolve(null);
            }

            const requestObject = submitFormJsonWithAbort<TCart, { redirectUrl: string }>(
                url,
                {
                    pid: pid,
                    uuid: uuid,
                    quantity: quantityStepper.getValue()
                },
                'POST',
                false
            );

            this.updateQtyRequests.push(requestObject);

            return requestObject.promise
                .then((response: Awaited<typeof requestObject.promise>) => {
                    if (!response) {
                        return null; // Aborted request
                    }

                    if (!response.success) {
                        return this.handleFailedQtyUpdateRequest(uuid, response);
                    }

                    this.renderCart(response);
                    this.accessibilityAlert(this.prefs().accessibilityAlerts.cartupdated, SCREEN_READER_ALERT);
                    this.trackQtyChange(response, quantityStepper);

                    return response;
                })
                .catch(error => {
                    showErrorLayout(error.message);

                    return null;
                })
                .finally(() => {
                    this.hideProgressBar();

                    const index = this.updateQtyRequests.indexOf(requestObject);

                    if (index > -1) {
                        this.updateQtyRequests.splice(index, 1);
                    }
                });
        }

        /**
         * @description Tracks quantity change event
         * @param response - failed response object
         * @param quantityStepper - widget instance of quantity stepper
         */
        trackQtyChange(response, quantityStepper) {
            const initValue = parseInt(quantityStepper.initValue, 10);
            const currQty = parseInt(quantityStepper.currentValue, 10);

            if (initValue !== currQty) {
                if (initValue < currQty) {
                    this.eventBus().emit('product.added.to.cart', response, quantityStepper);
                    this.eventBus().emit('bonus.product.added.to.cart', response, quantityStepper);
                } else {
                    this.eventBus().emit('cart.remove.product', response, quantityStepper);
                }
            }
        }

        /**
         * @description Fires `alert.show` event to be handled with Global Alerting System
         * @param accessibilityAlert - alert string
         * @param type - alert type string
         * @emits "alert.show"
         */
        accessibilityAlert(accessibilityAlert: string, type?: string) {
            this.eventBus().emit(
                'alert.show',
                {
                    accessibilityAlert
                },
                type
            );
        }

        /**
         * @description Scrolls cart error messages into viewport
         */
        scrollToError() {
            const cartErrorMessage = this.ref('errorMsgs').get();

            if (cartErrorMessage) {
                scrollWindowTo(cartErrorMessage, true, false, true);
            }
        }

        /**
         * @description Handler for "Edit" product button in a Cart
         * @param editBtn - Target "Edit" button
         */
        editProduct(editBtn: TRefElementInstance) {
            this.showProgressBar();

            const uuid = editBtn.data<string>('uuid');
            const editModeQV = editBtn.data<string>('editModeQv') ?? 'false';
            const is1774Product = editBtn.data<string>('is1774Product') ?? 'false';
            const isCareEssentialsProduct = editBtn.data<string>('isCareEssentialsProduct') ?? 'false';
            const isGiftCertificate = editBtn.data<boolean>('giftCertificate') || false;
            const href = editBtn.data<string>('href');
            const wrapperClasses: Array<string> = [];

            wrapperClasses.push(this.prefs().classesModalQuickView, this.prefs().classesModalQuickViewCart);

            if (isCareEssentialsProduct) {
                wrapperClasses.push(this.prefs().classesModalForCareEssentialsProduct);
            }

            this.getById('editProductModal', (editProductModal: TModalInstance) => {
                if (is1774Product) {
                    wrapperClasses.push(this.prefs().classesModalFor1774Product);
                }

                if (isGiftCertificate) {
                    wrapperClasses.push(this.prefs().classesModalGiftCertificate);
                } else {
                    editProductModal.ref('container').removeClass(this.prefs().classesModalGiftCertificate);
                }

                editProductModal.showModalPreview(wrapperClasses);
            });

            getContentByUrl(href, { uuid, editModeQV })
                .then(response => {
                    this.getById('editProductModal', (editProductModal: TModalInstance) => {
                        const modalOptions = {
                            body: response,
                            attributes: {
                                'data-tau-unique': 'edit_product_dialog',
                                'data-modal-aria-label': editBtn.data<string>('edit-modal-label')
                            }
                        };

                        const onShow = () => {
                            this.onShowModal.bind(this, editBtn);
                            editProductModal.callIfExists('focusFirstElement');
                        };

                        editProductModal.showModal(modalOptions, onShow, true);
                    });
                })
                .catch(error => {
                    this.getById('editProductModal', (editProductModal: TModalInstance) => {
                        editProductModal.close();
                        editProductModal.removeGlobalDialogClass();
                    });
                    this.renderCartWithItemLevelActionError(uuid, error.errorMessage);
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Handler for "See Similar Items" link on an out-of-stock product in a Cart
         * Loads the modal content for the clicked product, so that several sold out items in the cart
         * each open their own recommendations and their own "View More" link
         * @param link - Clicked "See Similar Items" link
         */
        showRecommendationModal(link: TRefElementInstance): void {
            const pid = link.data<string>('pid');
            const href = link.data<string>('href');

            if (!pid || !href) {
                return;
            }

            this.showProgressBar();
            this.getById('cartRecommendationModal', (recommendationModal: TModalInstance) => {
                recommendationModal.showModalPreview([this.prefs().classesModalCartRecommendation]);
            });

            getContentByUrl(href, { pid })
                .then(response => {
                    this.getById('cartRecommendationModal', (recommendationModal: TModalInstance) => {
                        const modalOptions = {
                            body: response,
                            attributes: {
                                'data-tau-unique': 'cart_recommendation_dialog'
                            }
                        };

                        const onShow = () => {
                            recommendationModal.callIfExists('focusFirstElement');
                        };

                        recommendationModal.showModal(modalOptions, onShow, true);
                    });
                })
                .catch(() => {
                    this.getById('cartRecommendationModal', (recommendationModal: TModalInstance) => {
                        recommendationModal.close();
                        recommendationModal.removeGlobalDialogClass();
                    });
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Handler for "Remove" product button in a Cart
         * Stores clicked button instance to property because of "Remove Popup" displaying and further interaction
         * @param removeButton - Clicked "Remove" product button or qtyStepper if value was 0
         */
        removeProduct(removeButton: TRefElementInstance | ICartInputStepperInstance): void {
            const InputStepper = this.getConstructor('inputStepper');
            const attributes = this.extractAttributes(removeButton);

            this.removeButton = removeButton;

            if (removeButton instanceof InputStepper) {
                attributes.currentValue = parseInt(removeButton.getValue(), 10);
                attributes.minOrderQuantity = removeButton.prefs().lineItemMinOrderQuantity;
            } else {
                const qtyStepperID = `quantityStepper-${attributes.uuid}`;

                this.getById(qtyStepperID, (stepper: TInputStepperInstance) => {
                    attributes.currentValue = parseInt(stepper.getValue(), 10);
                });
            }

            if (removeButton.data<string>('skipConfirmation')) {
                this.confirmedRemoveProduct({ attributes });
            } else {
                this.showConfirmationDialog(removeButton, attributes);
            }
        }

        /**
         * @description Handler for "Remove" product option button in a Cart
         * @param removeButton - Clicked "Remove" product option button
         * @returns Promise object represents server response for product option deletion
         */
        removeOption(removeButton: TRefElementInstance) {
            const actionUrls = this.prefs().actionUrls as Record<string, string | undefined>;
            const url = removeButton.data<string>('action')
                || actionUrls.removeProductOptionUrl
                || actionUrls.removeOptionProductLineItem;
            const pid = removeButton.data<string>('pid');
            const uuid = removeButton.data<string>('uuid');
            const pliUUID = removeButton.data<string>('pliuuid');

            return submitFormJson(
                url,
                {
                    pid,
                    uuid,
                    pliUUID,
                    optionId: removeButton.data<string>('optionId'),
                    optionValue: removeButton.data<string>('optionValue'),
                    customOptionValue: removeButton.data<string>('customOptionValue')
                },
                'POST'
            ).then((response: TServerResponse & TCart) => {
                if (response?.success === false) {
                    throw new Error(<string>response.errorMessage);
                }

                const cart = <TResponse & TCart>response;

                if (cart) {
                    this.renderCart(cart)
                        .then(() => this.accessibilityAlert(this.prefs().accessibilityAlerts.cartupdated, SCREEN_READER_ALERT));
                }

                return response;
            }).catch(error => {
                this.renderCartWithItemLevelActionError(uuid, error?.message ? error.message : '');

                return error;
            });
        }

        /**
         * @description Get selected product options
         * @returns Empty array as no options are selected in fit guide context
         */
        getSelectedProductOptions(): Array<Record<string, unknown>|null> {
            return [];
        }

        /**
         * @description Updates a product option
         * @param config - Product option edit payload
         * @param config.uuid - Product line item UUID
         * @param config.optionId - Product option ID
         * @param config.text - Updated personalization text
         * @param url - Endpoint for editing the product option
         * @returns Promise resolving to the updated cart response or the handled error
         */
        editOption(config, url: string) {
            const uuid = config.uuid;

            return submitFormJson(
                url,
                config,
                'POST'
            ).then((response: TServerResponse & TCart) => {
                if (response?.success === false) {
                    throw Error(<string>response.errorMessage);
                }

                const cart = <TResponse & TCart>response;

                if (cart) {
                    this.renderCart(cart)
                        .then(() => this.accessibilityAlert(this.prefs().accessibilityAlerts.cartupdated, SCREEN_READER_ALERT));
                }

                return response;
            }).catch(error => {
                this.renderCartWithItemLevelActionError(uuid, error?.message ? error.message : '');

                return error;
            });
        }

        /**
         * @description A callback, which is executed, when customer agreed to remove product in confirmation popup
         * @param object - Configuration object or instance of ConfirmDialog
         * @param object.attributes - Product details (based on attributes of confirmation dialog)
         * @param [movedToWishlist] - Indicates if product was moved to wishlist
         * @emits "cart.remove.product"
         * @returns Promise object represents server response for product deletion
         */
        confirmedRemoveProduct({ attributes }: { attributes: TAttributes }, movedToWishlist = false) {
            if (!attributes) {
                return Promise.resolve({ success: false });
            }

            const url = attributes.url;
            const pid = attributes.pid;
            const uuid = attributes.uuid;

            if (!movedToWishlist) {
                this.showProgressBar();
            }

            const payload: Record<string, string> = {};

            if (uuid) {
                payload.uuid = uuid;
            }

            if (pid) {
                payload.pid = pid;
            }

            let removePromise = submitFormJson(url, payload, 'POST').then(
                (response: TServerResponse & { basket: unknown; items: Array<unknown> }) => {
                    if (!response.success) {
                        throw Error(<string>response.errorMessage);
                    }

                    this.confirmationRemoveCallback(response, movedToWishlist);

                    this.eventBus().emit('cart.remove.product', response, this);

                    if (movedToWishlist) {
                        this.eventBus().emit('product.addedToWishlist');
                        this.eventBus().emit(CART_PAGE_NOTIFICATIONS, this, 'item_moved_to_wishlist');
                    } else if (response.items.length) {
                        this.eventBus().emit(CART_PAGE_NOTIFICATIONS, this, 'item_removed');
                    }

                    if (this.config.numItems) {
                        this.accessibilityAlert(this.prefs().accessibilityAlerts.productremoved, SCREEN_READER_ALERT);
                        setTimeout(() => {
                            this.callIfExists('focusFirst', 'self', ['.b-cart_products .b-cart_product-title a']);
                        }, 300);
                    } else {
                        this.accessibilityAlert(this.prefs().accessibilityAlerts.cartemptied, SCREEN_READER_ALERT);
                    }

                    return response;
                }
            );

            if (!movedToWishlist) {
                removePromise = removePromise
                    .catch(error => {
                        this.renderCartWithItemLevelActionError(uuid, error.message);

                        return error;
                    })
                    .finally(() => {
                        this.hideProgressBar();
                    });
            }

            return removePromise;
        }

        /**
         * @description A callback, which is executed once `remove` product server call was performed
         * @param cart - server response object
         * @param [movedToWishlist] - Indicates if product was moved to wishlist
         */
        confirmationRemoveCallback(cart, movedToWishlist = false): void {
            if (movedToWishlist) {
                this.renderCart({ ...cart, pageSuccess: this.prefs().accessibilityAlerts.movedtowishlist }).then(() => this.scrollToError());
            } else {
                this.renderCart({ ...cart, pageSuccess: this.prefs().accessibilityAlerts.productremoved }).then(() => this.scrollToError());
            }
        }

        /**
         * @description Move product from cart to wishlist.
         * In fact this functionality implemented in 2 steps:
         * 1. Add product to wishlist
         * 2. Remove product from cart
         * This is done due to possible backend code duplication in one universal endpoint
         * @param confirmationDialog Target popup
         * @returns Promise object represents server response for product adding to wishlist
         */
        moveProductToWishlist(confirmationDialog: TConfirmDialogInstance): Promise<Array<Record<string, unknown>>> {
            if (!confirmationDialog) {
                return Promise.resolve([{ error: true }]);
            }

            const removeButtonUUID = confirmationDialog.attributes.uuid;

            const productAttributes = this.getProductAttributes(confirmationDialog);

            this.showProgressBar();

            return submitFormJson(this.prefs().actionUrls.moveProductToWishlist, productAttributes)
                .then((response: TServerResponse) => {
                    if (!response.success && !response.itemAlreadyExists) {
                        throw Error(<string>response.errorMessage);
                    }

                    if (this.removeButton) {
                        this.eventBus().emit('product.added.to.wishlist', this.removeButton);
                    }

                    const removeFromCartPromise = this.confirmedRemoveProduct(confirmationDialog, true);

                    return Promise.all([response, removeFromCartPromise]);
                })
                .catch((error: Error) => {
                    this.renderCartWithItemLevelActionError(removeButtonUUID, <string>error.message);

                    return [{ error }];
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        private extractAttributes(removeButton: TRefElementInstance | ICartInputStepperInstance): TAttributes {
            const attributes: TAttributes = {
                'data-tau': this.prefs().tauAttrRemoveProductConfirmationPopUp,
                url: removeButton.data<string>('removeAction') ?? removeButton.data<string>('action'),
                pid: removeButton.data<string>('pid'),
                uuid: removeButton.data<string>('uuid'),
                currentValue: removeButton.data<number>('quantity'),
                minOrderQuantity: null,
                optionId: removeButton.data<string>('optionId'),
                optionVal: removeButton.data<string>('optionValue'),
                customOptionValue: removeButton.data<string>('customOptionValue'),
                lowest30daysPriceMessage: removeButton.data<string>('lowest-30DaysPriceMessage') ?? '',
                isPriceAtLowestLevel: removeButton.data<string>('isPriceAtLowestLevel') ?? 'false',
                isDisplay30DaysPriceCart: removeButton.data<string>('displayLowestPriceCart') ?? 'false',
                showStrikePriceAndBadge: removeButton.data<string>('showStrikePriceAndBadge') ?? 'true',
                shortName: removeButton.data<string>('shortName'),
                productModelName: removeButton.data<string>('productModelName'),
                imageUrl: removeButton.data<string>('imageUrl'),
                imageAlt: removeButton.data<string>('imageAlt'),
                pdpLink: removeButton.data<string>('pdpLink'),
                variationAttributes: this.extractVariationAttributes(removeButton),
                price: this.extractPriceAttributes(removeButton),
                imageSources: this.extractImageSources(removeButton)
            };

            return attributes;
        }

        private extractVariationAttributes(
            removeButton: TRefElementInstance | ICartInputStepperInstance
        ): Array<{ name: string; value: string }> | undefined {
            const variationAttributes: Array<{ name: string; value: string }> = [];

            for (let i = 0; i < 3; i++) {
                const name = removeButton.data<string>(`variationAttributeName-${i}`);
                const value = removeButton.data<string>(`variationAttributeValue-${i}`);

                if (name && value) {
                    variationAttributes.push({ name, value });
                }
            }

            return variationAttributes.length ? variationAttributes : undefined;
        }

        private extractPriceAttributes(removeButton: TRefElementInstance | ICartInputStepperInstance): TAttributes['price'] {
            const price: TAttributes['price'] = {};
            const listFormatted = removeButton.data<string>('priceListFormatted');
            const salesFormatted = removeButton.data<string>('priceSalesFormatted');
            const salesDiscount = removeButton.data<string>('priceSalesDiscount');
            const minFormatted = removeButton.data<string>('priceMinFormatted');
            const maxFormatted = removeButton.data<string>('priceMaxFormatted');

            if (listFormatted) {
                price.list = { formatted: listFormatted };
            }

            if (salesFormatted || salesDiscount) {
                price.sales = {
                    formatted: salesFormatted,
                    discount: salesDiscount
                };
            }

            if (minFormatted) {
                price.min = { sales: { formatted: minFormatted } };
            }

            if (maxFormatted) {
                price.max = { sales: { formatted: maxFormatted } };
            }

            price.isDefaultPrice = removeButton.data<string>('defaultPrice');
            price.isRangePrice = removeButton.data<string>('rangePrice');

            return price;
        }

        private extractImageSources(
            removeButton: TRefElementInstance | ICartInputStepperInstance
        ): Array<{ media: string; srcset: string }> | undefined {
            const imageSources: Array<{ media: string; srcset: string }> = [];

            for (let i = 0; i < 3; i++) {
                const media = removeButton.data<string>(`imageSourceMedia-${i}`);
                const srcset = removeButton.data<string>(`imageSourceSrcset-${i}`);

                if (media && srcset) {
                    imageSources.push({ media, srcset });
                }
            }

            return imageSources.length ? imageSources : undefined;
        }

        private showConfirmationDialog(removeButton: TRefElementInstance | ICartInputStepperInstance, attributes: TAttributes): void {
            this.getById('confirmDialog', (confirmDialog: TConfirmDialogInstance) => {
                confirmDialog.showModal({
                    productName: removeButton.data<string>('name'),
                    attributes,
                    isCareEssentialsProduct: removeButton.data<string>('isCareEssentialsProduct') ?? '',
                    isBonusProductLineItem: removeButton.data<string>('bonusProductLineItem') ?? '',
                    netContent: removeButton.data<string>('netContent') ?? ''
                });
            });
        }

        /**
         * @description Listens for the "express.checkout.basket.started" event and
         * sends initiates the appropriate GTM events.
         */
        sendGTMForExpressCheckout() {
            this.config.checkoutType = 'express_cart';
            this.eventBus().emit('cart.page.submitted', this);
        }
    }

    return CoreCartMgr;
}

export type TCoreCartMgr = ReturnType<typeof CoreCartMgrClassCreator>;

export type TCoreCartMgrInstance = InstanceType<TCoreCartMgr>;

export default CoreCartMgrClassCreator;
